<template>
  <v-app>
    <v-main>
      <form>
        <v-container grid-list-md class="ghost">
          <v-row class="justify-space-between">
            <v-col cols="10" sm="3">
              <v-text-field
                label="Patient ID / Name"
                outlined
                class="iol-v-text-field"
                ref="first"
                v-model="inputs.patientID"
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Keratometric Index"
                outlined
                class="smooth iol-v-select"
                v-model="inputs.keratometric_index"
                :items="$parent.keratometricItems"
                return-object
                required
              />
            </v-col>
            <v-col cols="10" sm="3">
              <v-select
                label="Biometer"
                :items="$parent.biometerItems"
                item-text="text"
                outlined
                required
                class="smooth iol-v-select"
                v-model="inputs.biometer"
                return-object
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-divider />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <br />
            </v-col>
          </v-row>
          <v-sheet
            v-bind:class="{
              'iol-right-v-sheet': inputs.side,
              'iol-left-v-sheet': !inputs.side,
            }"
          >
            <v-row class="align-baseline">
              <v-col cols="12">
                <v-btn
                  v-if="$vuetify.breakpoint.mobile"
                  @click="goToCamera"
                  color="#3B4256"
                  class="white--text"
                  style="width: 100%;"
                  x-large
                >
                  <v-icon class="mr-2">mdi-camera</v-icon>
                  Auto fill
                </v-btn>
              </v-col>
              <v-col cols="2" md="5" class="switch-eye-label-left">
                <transition name="fade">
                  <div v-if="inputs.side">
                    <h1>OD</h1>
                  </div>
                </transition>
              </v-col>
              <v-spacer />
              <v-col cols="2" md="1">
                <v-switch
                  v-model="inputs.side"
                  class="switch-eye"
                  :messages="
                    inputs.side ? 'switch to left eye' : 'switch to right eye'
                  "
                />
              </v-col>
              <v-spacer />
              <v-col cols="2" md="5" class="switch-eye-label-right">
                <transition name="fade">
                  <div v-if="!inputs.side">
                    <h1>OS</h1>
                  </div>
                </transition>
              </v-col>
            </v-row>
            <v-row class="justify-center">
              <v-col cols="11">
                <p class="text-justify font-italic">
                  If the first eye has already been operated, enter its
                  preoperative biometry, the A constant and power of the
                  implanted IOL, and the resulting postoperative spherical
                  equivalent. The Second Eye function is only available for
                  physiological, non-operated eyes and post-myopic /
                  post-hyperopic LVC eyes, implanted using the same IOL model.
                  If the AL difference is > 0.65mm or the ARC difference is >
                  0.22mm, it will automatically be deactivated.
                </p>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-sheet class="iol-v-sheet">
                  <v-row>
                    <v-col cols="6" md="3">
                      <v-select
                        label="IOL Model"
                        :items="$parent.IOLModelItems"
                        item-text="text"
                        outlined
                        class="smooth iol-v-select"
                        v-model="inputs.iol_model"
                        return-object
                        required
                        @change="updateAConst(inputs)"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="6" md="3">
                      <v-text-field
                        label="A constant"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.aconst"
                        :error-messages="getErrors('aconst', $v.inputs.aconst)"
                        :disabled="inputs.iol_model.type == '60MA'"
                        :required="inputs.iol_model.type != '60MA'"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.aconst.$touch()"
                        @blur="$v.inputs.aconst.$touch()"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="10" md="4">
                      <v-text-field
                        label="Target refraction (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.target"
                        :error-messages="getErrors('target', $v.inputs.target)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.target.$touch()"
                        @blur="$v.inputs.target.$touch()"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" sm="5">
                      <v-text-field
                        label="AL (mm)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.al"
                        :error-messages="getErrors('al', $v.inputs.al)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.al.$touch()"
                        @blur="$v.inputs.al.$touch()"
                      />
                      <v-text-field
                        label="K1 (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.k1"
                        :error-messages="getErrors('k1', $v.inputs.k1)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k1.$touch()"
                        @blur="$v.inputs.k1.$touch()"
                      />
                      <v-text-field
                        label="K2 (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.k2"
                        :error-messages="getErrors('k2', $v.inputs.k2)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k2.$touch()"
                        @blur="$v.inputs.k2.$touch()"
                      />
                      <v-text-field
                        label="ACD (mm)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.acd"
                        :error-messages="getErrors('acd', $v.inputs.acd)"
                        required
                        type="number"
                        step="0.01"
                        @input="$v.inputs.acd.$touch()"
                        @blur="$v.inputs.acd.$touch()"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="6" sm="6">
                      <v-text-field
                        label="Custom vitreous"
                        outlined
                        class="iol-v-text-field"
                        value="1.336"
                        readonly
                        disabled
                      />
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional</h3>
                        <br />
                        <v-text-field
                          label="LT (mm)"
                          class="iol-v-text-field"
                          outlined
                          v-model="inputs.lt"
                          :error-messages="getErrors('lt', $v.inputs.lt)"
                          type="number"
                          step="0.01"
                          @input="$v.inputs.lt.$touch()"
                          @blur="$v.inputs.lt.$touch()"
                        />
                        <v-text-field
                          label="CCT (µm)"
                          class="iol-v-text-field"
                          outlined
                          type="number"
                          step="0"
                          v-model="inputs.cct"
                          :error-messages="getErrors('cct', $v.inputs.cct)"
                          @input="$v.inputs.cct.$touch()"
                          @blur="$v.inputs.cct.$touch()"
                        />
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-row class="justify-md-space-between">
                    <v-col cols="12" md="3">
                      <v-row>
                        <v-switch
                          label="ICL"
                          v-model="inputs.is_icl"
                          prepend-icon="info"
                        >
                          <v-tooltip bottom slot="prepend">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-on="on" v-bind="attrs" color="primary">
                                info
                              </v-icon>
                            </template>
                            <span
                              >If an Implantable Collamer Lens was implanted at
                              the time of biometry, activate this option. It
                              will adapt the AL measurement</span
                            >
                          </v-tooltip>
                        </v-switch>
                      </v-row>
                    </v-col>
                    <v-col cols="12" md="9">
                      <v-sheet class="iol-opt-v-sheet">
                        <v-row>
                          <v-col cols="8" md="8">
                            <h3>Optional: Laser Vision Correction History ?</h3>
                          </v-col>
                          <v-col cols="4" md="4">
                            <v-switch
                              class="ma-0 pa-0"
                              hide-details
                              v-model="inputs.is_corneal_laser"
                            >
                            </v-switch>
                          </v-col>
                        </v-row>
                        <br />
                        <v-row class="justify-left">
                          <v-col cols="8" md="8" offset="1">
                            <v-select
                              label="Corneal refractive surgery"
                              :items="$parent.cornealLaserTypeItems"
                              item-text="text"
                              outlined
                              class="smooth iol-v-select"
                              v-model="inputs.corneal_laser_type"
                              :disabled="!inputs.is_corneal_laser"
                              :required="inputs.is_corneal_laser"
                              return-object
                            />
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Pre-LVC anterior corneal radius (mm)"
                                  outlined
                                  class="iol-v-text-field"
                                  v-model="inputs.pre_lvc_arc"
                                  :error-messages="
                                    getErrors(
                                      'pre_lvc_arc',
                                      $v.inputs.pre_lvc_arc,
                                    )
                                  "
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  type="number"
                                  step="0.01"
                                  @change="$v.inputs.pre_lvc_arc.$touch()"
                                  @blur="$v.inputs.pre_lvc_arc.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the anterior corneal radius
                                measured before laser vision correction, in
                                millimeters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Corrected ametropia (D)"
                                  outlined
                                  class="iol-v-text-field"
                                  v-model="inputs.laser_amount"
                                  :error-messages="
                                    getErrors(
                                      'laser_amount',
                                      $v.inputs.laser_amount,
                                    )
                                  "
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  type="number"
                                  step="0.01"
                                  @change="$v.inputs.laser_amount.$touch()"
                                  @blur="$v.inputs.laser_amount.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the absolute value of the
                                corrected ametropia, in diopters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet class="iol-v-sheet" elevation="1" outlined>
                  <div class="bg-blue text-center rounded-lg">
                    <span>
                      <h3>Controlateral Operated Eye Information</h3>
                    </span>
                  </div>
                  <br />
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Implanted A constant"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.aconst"
                        required
                        readonly
                        disabled
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="12" md="5">
                      <v-text-field
                        label="Implanted IOL power"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.power2"
                        required
                        :error-messages="getErrors('power2', $v.inputs.power2)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.power2.$touch()"
                        @blur="$v.inputs.power2.$touch()"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Postop. spherical equivalent (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.se2"
                        required
                        :error-messages="getErrors('se2', $v.inputs.se2)"
                        type="number"
                        step="0.001"
                        @input="$v.inputs.se2.$touch()"
                        @blur="$v.inputs.se2.$touch()"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" sm="6">
                      <v-text-field
                        label="Preop. AL (mm)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.al2"
                        :error-messages="getErrors('al2', $v.inputs.al2)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.al2.$touch()"
                        @blur="$v.inputs.al2.$touch()"
                      />
                      <v-text-field
                        label="Preop. K1 (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.k12"
                        required
                        :error-messages="getErrors('k12', $v.inputs.k12)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k12.$touch()"
                        @blur="$v.inputs.k12.$touch()"
                      />
                      <v-text-field
                        label="Preop. K2 (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.k22"
                        required
                        :error-messages="getErrors('k22', $v.inputs.k22)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.k22.$touch()"
                        @blur="$v.inputs.k22.$touch()"
                      />
                      <v-text-field
                        label="Preop. ACD (D)"
                        class="iol-v-text-field"
                        outlined
                        v-model="inputs.acd2"
                        required
                        :error-messages="getErrors('acd2', $v.inputs.acd2)"
                        type="number"
                        step="0.01"
                        @input="$v.inputs.acd2.$touch()"
                        @blur="$v.inputs.acd2.$touch()"
                      />
                    </v-col>
                    <v-spacer />
                    <v-col cols="6" sm="5">
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional</h3>
                        <br />
                        <v-text-field
                          label="LT (mm)"
                          class="iol-v-text-field"
                          outlined
                          type="number"
                          step="0.01"
                          v-model="inputs.lt2"
                          :error-messages="getErrors('lt2', $v.inputs.lt2)"
                          @input="$v.inputs.lt2.$touch()"
                          @blur="$v.inputs.lt2.$touch()"
                        />
                        <v-text-field
                          label="CCT (µm)"
                          class="iol-v-text-field"
                          outlined
                          v-model="inputs.cct2"
                          :error-messages="getErrors('cct2', $v.inputs.cct2)"
                          type="number"
                          step="0"
                          @input="$v.inputs.cct2.$touch()"
                          @blur="$v.inputs.cct2.$touch()"
                        />
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-row class="justify-md-space-between">
                    <v-col cols="12" md="3">
                      <v-row>
                        <v-switch
                          label="ICL"
                          v-model="inputs.is_icl"
                          prepend-icon="info"
                        >
                          <v-tooltip bottom slot="prepend">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-on="on" v-bind="attrs" color="primary">
                                info
                              </v-icon>
                            </template>
                            <span
                              >If an Implantable Collamer Lens was implanted at
                              the time of biometry, activate this option. It
                              will adapt the AL measurement</span
                            >
                          </v-tooltip>
                        </v-switch>
                      </v-row>
                    </v-col>
                    <v-col cols="12" md="9">
                      <v-sheet class="iol-opt-v-sheet">
                        <h3>Optional: Laser Vision Correction History ?</h3>
                        <br />
                        <v-row class="justify-left">
                          <v-col cols="8" md="8" offset="1">
                            <v-select
                              label="Corneal Refractive surgery"
                              :items="$parent.cornealLaserTypeItems"
                              item-text="text"
                              outlined
                              class="smooth iol-v-select"
                              v-model="inputs.corneal_laser_type"
                              :disabled="!inputs.is_corneal_laser"
                              :required="inputs.is_corneal_laser"
                              return-object
                            />
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Pre-LVC anterior corneal radius (mm)"
                                  outlined
                                  class="iol-v-text-field"
                                  v-model="inputs.pre_lvc_arc2"
                                  :error-messages="
                                    getErrors(
                                      'pre_lvc_arc2',
                                      $v.inputs.pre_lvc_arc2,
                                    )
                                  "
                                  type="number"
                                  step="0.01"
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  @change="$v.inputs.pre_lvc_arc2.$touch()"
                                  @blur="$v.inputs.pre_lvc_arc2.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the anterior corneal radius
                                measured before laser vision correction, in
                                millimeters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                        <v-row class="justify-center">
                          <v-col cols="8" md="8">
                            <v-tooltip bottom slot="append">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  label="Corrected ametropia (D)"
                                  class="iol-v-text-field"
                                  outlined
                                  v-model="inputs.laser_amount2"
                                  :error-messages="
                                    getErrors(
                                      'laser_amount2',
                                      $v.inputs.laser_amount2,
                                    )
                                  "
                                  :disabled="!inputs.is_corneal_laser"
                                  :required="inputs.is_corneal_laser"
                                  type="number"
                                  step="0.01"
                                  @change="$v.inputs.laser_amount2.$touch()"
                                  @blur="$v.inputs.laser_amount2.$touch()"
                                  v-bind="attrs"
                                  v-on="on"
                                />
                              </template>
                              <span
                                >If available, enter the absolute value of the
                                corrected ametropia, in diopters</span
                              >
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
          </v-sheet>
          <v-row>
            <v-col cols="12">
              <v-divider />
              <br />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn @click="submit" color="#3B4256" class="white--text" x-large
                >Calculate</v-btn
              >
            </v-col>
          </v-row>
          <br />
          <v-row>
            <v-col cols="12" class="text-center">
              <a @click="clear" :style="$parent.resetstyle">Reset</a>
            </v-col>
          </v-row>
        </v-container>
      </form>
      <v-snackbar v-model="errorSnackbar.show" :color="errorSnackbar.color" top>
        {{ errorSnackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="errorSnackbar.show = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<style>
.v-application .primary--text {
  color: #3b4256 !important;
  caret-color: #3b4256 !important;
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import {
  required,
  requiredIf,
  minValue,
  between,
} from 'vuelidate/lib/validators'
import { dualSolve } from '@/graphql/queries/solve.gql'
import Logout from '@/vue/Logout.vue'
import { updateAConstExt } from '@/core/sync_methods.mjs'

let omitNull = (obj) => {
  let copy = { ...obj }
  Object.keys(copy)
    .filter((k) => copy[k] === null)
    .forEach((k) => delete copy[k])
  return copy
}

export default {
  mixins: [validationMixin],
  components: {
    Logout,
  },
  validations() {
    return {
      inputs: {
        al: { required, between: between(17, 40) },
        acd: { required, between: between(1, 6) },
        k1: { required, between: between(30, 60) },
        k2: {
          required,
          between: between(30, 60),
          minValue: minValue(this.inputs.k1),
        },
        lt: { between: between(2.5, 8) },
        cct: { between: between(350, 700) },
        target: { required, between: between(-4, 1.5) },
        aconst: {
          required: requiredIf(function(form) {
            return this.inputs.iol_model.type != '60MA'
          }),
          between: between(117, 121),
        },
        pre_lvc_arc: { between: between(5.5, 9.5) },
        laser_amount: { between: between(0.5, 12) },
        al2: { required, between: between(17, 40) },
        acd2: { required, between: between(1, 6) },
        k12: { required, between: between(30, 60) },
        k22: {
          required,
          between: between(30, 60),
          minValue: minValue(this.inputs.k12),
        },
        lt2: { between: between(2.5, 8) },
        cct2: { between: between(350, 700) },
        se2: { required, between: between(-9, 9) },
        power2: { required, between: between(0, 35) },
        pre_lvc_arc2: { between: between(5.5, 9.5) },
        laser_amount2: { between: between(0.5, 12) },
      },
    }
  },
  mounted() {
    if (this.$route.query.ocr) {
      this.$v.inputs.$touch()
    }
    // Initialize side from currentSide (from last calculation)
    this.inputs.side = this.currentSide
    
    // Load inputs based on currentSide
    if (this.currentSide) {
      this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs) }
    } else {
      this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs2) }
    }
    this.inputs.is_second = true
    
    // Clean up properties from Complex that don't exist in Second
    delete this.inputs.is_non_physio_cornea
    delete this.inputs.is_rk
    delete this.inputs.prc_topo
    delete this.inputs.tk
    delete this.inputs.tcrp
  },
  data: () => {
    let ret = {
      inputs: {
        patientID: '',
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        is_corneal_laser: false,
        is_icl: false,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        is_non_physio_cornea: false,
        is_rk: false,
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        is_second: true,
        pre_lvc_arc: '',
        laser_amount: '',
        pre_lvc_arc2: '',
        laser_amount2: '',
        target: 0,
      },
      inputs_default: {
        patientID: '',
        side: true,
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        is_corneal_laser: false,
        is_icl: false,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        is_non_physio_cornea: false,
        is_rk: false,
        keratometric_index: 1.3375,
        biometer: { type: 'Other', text: 'Other' },
        is_second: true,
        pre_lvc_arc: '',
        laser_amount: '',
        pre_lvc_arc2: '',
        laser_amount2: '',
        target: 0,
      },
      errorSnackbar: {
        show: false,
        text: '',
        color: 'error',
      },
    }
    if (process.env.NODE_ENV == 'development') {
      ret.inputs = {
        ...ret.inputs,
        side: true,
        keratometric_index: 1.3375,
        al: 29.32,
        k1: 43.32,
        k2: 45.34,
        acd: 3.24,
        lt: 4.12,
        cct: 534,
        biometer: { type: 'Other', text: 'Other' },
        iol_model: { type: 'Finevision', text: 'Finevision', aconst: 118.9 },
        aconst: 118.9,
        is_corneal_laser: true,
        corneal_laser_type: { type: 'Myopic', text: 'Myopic' },
        pre_lvc_arc: 7,
        laser_amount: 9,
        is_icl: true,
        al2: 23.32,
        k12: 42.64,
        k22: 45.28,
        acd2: 4.18,
        lt2: 5.72,
        cct2: 569,
        power2: 24,
        se2: -3,
        pre_lvc_arc2: 7,
        laser_amount2: 7.5,
        target: -3,
        patientID: 'testid',
      }
    }
    return ret
  },

  computed: {
    ...mapState(['appHeader', 'ginputs', 'ginputs2', 'currentSide']),
  },

  watch: {
    'inputs.side': function(newValue) {
      // OD = true, OS = false
      if (newValue) {
        // Switch to OD - load ginputs
        this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs) }
        this.inputs.is_second = true
      } else {
        // Switch to OS - load ginputs2
        this.inputs = { ...omitNull(this.inputs), ...omitNull(this.ginputs2) }
        this.inputs.is_second = true
      }
      
      // Update currentSide in store
      this.$store.dispatch('updateCurrentSide', newValue)
      
      // Clean up properties from Complex that don't exist in Second
      delete this.inputs.is_non_physio_cornea
      delete this.inputs.is_rk
      delete this.inputs.prc_topo
      delete this.inputs.tk
      delete this.inputs.tcrp
    },
    inputs: {
      handler: function(newValue) {
        // Auto-save to the correct store when inputs change
        if (newValue.side) {
          this.$store.dispatch('updateInputs', newValue)
        } else {
          this.$store.dispatch('updateInputs2', newValue)
        }
      },
      deep: true,
    },
  },

  methods: {
    getErrors(name, model) {
      const errors = []
      if (!model.$dirty) return errors
      switch (name) {
        case 'k2':
          !model.minValue && errors.push('K2 must be greater than K1')
        case 'k22':
          !model.minValue && errors.push('K22 must be greater than K12')
        case 'al':
        case 'acd':
        case 'k1':
        case 'k2':
        case 'target':
        case 'aconst':
        case 'power2':
        case 'se2':
        case 'al2':
        case 'k12':
        case 'k22':
        case 'acd2':
          !model.required && errors.push('required')
          !model.between && errors.push('value out of bounds')
          break
        case 'cct':
          this.$v.inputs.tk && !model.required && errors.push('required')
        case 'lt':
        case 'cct':
        case 'lt2':
        case 'cct2':
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        case 'pre_lvc_arc':
        case 'laser_amount':
        case 'pre_lvc_arc2':
        case 'laser_amount2':
          this.inputs.is_corneal_laser &&
            model.required &&
            errors.push('required')
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        case 'prc_topo':
        case 'tk':
        case 'tcrp':
          model !== '' && !model.between && errors.push('value out of bounds')
          break
        default:
          break
      }
      return errors
    },
    isNonPhysioCorneaUpdated() {
      if (this.inputs.is_non_physio_cornea && this.inputs.is_second) {
        this.inputs.is_second = false
      }
    },
    isRKUpdated() {
      if (this.inputs.is_rk && this.inputs.is_second) {
        this.inputs.is_second = false
      }
    },
    updateAConst(input) {
      updateAConstExt(this.$parent, input)
    },
    focusFirstError(component = this) {
      if (component.hasError) {
        component.focus()
        return true
      }

      let focused = false

      component.$children.some((childComponent) => {
        focused = this.focusFirstError(childComponent)
        return focused
      })

      return focused
    },
    submit() {
      this.$v.inputs.$touch()
      this.$nextTick(() => {
        if (this.focusFirstError()) {
          return
        }
        Object.keys(this.inputs).forEach(
          (k) =>
            (this.inputs[k] = this.inputs[k] === '' ? null : this.inputs[k]),
        )

        const inputs = { ...omitNull(this.inputs) }
        // Store inputs based on which eye is selected
        if (this.inputs.side) {
          // OD - save to ginputs
          this.$store.dispatch('updateInputs', this.inputs)
        } else {
          // OS - save to ginputs2
          this.$store.dispatch('updateInputs2', this.inputs)
        }

        // send type to get_result
        inputs['iol_model'] = inputs['iol_model'].type
        inputs['biometer'] = inputs['biometer'].type
        inputs['corneal_laser_type'] = inputs['corneal_laser_type'].type

        // do not send corneal history if is_corneal false
        if (inputs['is_corneal_laser'] === false) {
          delete inputs['corneal_laser_type']
          delete inputs['laser_amount']
          delete inputs['pre_lvc_arc']
          delete inputs['laser_amount2']
          delete inputs['pre_lvc_arc2']
        }

        // delete those inputs because not used in calculation, so not sent via graphql,
        // only stored in ginputs for reuse on other tab
        delete inputs['patientID']

        const inputs2 = { ...inputs }

        inputs2['is_second'] = false
        let fields_to_remove = [
          'al2',
          'k12',
          'k22',
          'acd2',
          'lt2',
          'cct2',
          'power2',
          'se2',
          'laser_amount2',
          'pre_lvc_arc2',
          // Clean up properties from Complex that should not be sent
          'is_non_physio_cornea',
          'is_rk',
          'prc_topo',
          'tk',
          'tcrp',
        ]
        fields_to_remove.forEach((field) => delete inputs2[field])

        this.$apollo
          .query({
            query: dualSolve,
            variables: {
              inputs,
              inputs2,
            },
          })
          .then((result) => {
            // Save to the correct store based on which eye is selected
            if (this.inputs.side) {
              // OD - save OD result to results, OS result to results2
              this.$store.dispatch('setResults', result.data.solveOD)
              this.$store.dispatch('setResults2', result.data.solveOS)
            } else {
              // OS - save OS result to results, OD result to results2
              this.$store.dispatch('setResults', result.data.solveOS)
              this.$store.dispatch('setResults2', result.data.solveOD)
            }
            // Store which eye was calculated
            this.$store.dispatch('updateCurrentSide', this.inputs.side)
            this.$store.dispatch('updateLastCalc', 'second')
            this.$router.push('result_second')
          })
          .catch((e) => {
            this.$raven.captureException(e)
            this.errorSnackbar.text =
              e.message || 'An error occurred during calculation'
            this.errorSnackbar.show = true
          })
      })
    },
    clear() {
      Object.keys(this.inputs).forEach(
        (k) =>
          (this.inputs[k] =
            this.inputs_default[k] !== null ? this.inputs_default[k] : null),
      )
      // Reset store based on current side
      if (this.inputs.side) {
        this.$store.dispatch('resetInputs')
      } else {
        this.$store.dispatch('resetInputs2')
      }
      this.$refs.first.focus()
    },
    goToCamera() {
      this.$router.push({ name: 'CameraCapture' })
    },
    ...mapActions(['updateInputs', 'updateInputs2', 'setResults', 'setResults2', 'setAuthUser', 'resetInputs', 'resetInputs2', 'updateCurrentSide']),
  },
}
</script>
